import {Component, Input, OnInit} from '@angular/core';
import {
    ConfigureMeterSplit,
    ConnectionType,
    Meter,
    MeterAllocation,
    MeterDetails,
    MeterType
} from '@flowmaps-com/flowmaps-typescriptmodels';
import {cloneDeep} from 'lodash';
import {openConfirmationModalWithCallback} from '../../../../app-utils';
import {Handler} from '../../../../common/handler';
import {
    ModalConfirmAutofocus,
    ModalConfirmAutofocusData
} from '../../../../common/modal-confirm/modal-confirm.component';
import {uuid} from '../../../../common/utils';
import {View} from '../../../../common/view';
import {Entity} from '../../../../handlers/entity';
import {isPercentageAllocated, meterDisplayParentId} from '../submeter-role.utils';

const MAX_ALLOCATIONS = 10;

@Component({
    selector: 'app-meter-split',
    templateUrl: './meter-split.component.html'
})
@Handler()
export class MeterSplitComponent extends View implements OnInit {
    @Input() data: MeterSplitComponentData;

    allocations: MeterAllocation[] = [];
    hasExistingSplit = false;
    readonly maxAllocations = MAX_ALLOCATIONS;

    ngOnInit(): void {
        const existing = allocatedChildren(this.data.entity.meter, this.data.entity.connection.meters);
        this.hasExistingSplit = existing.length > 0;
        this.allocations = existing.length > 0
            ? existing.map(meter => ({
                meterId: meter.meterId,
                details: cloneDeep(meter.details) || defaultDetails(),
                percentage: meter.allocationPercentage
            }))
            : [newAllocation(50), newAllocation(50)];
    }

    addAllocation(): void {
        if (this.allocations.length < MAX_ALLOCATIONS) {
            this.allocations.push(newAllocation(undefined));
        }
    }

    removeAllocation(index: number): void {
        if (this.allocations.length > 2) {
            this.allocations.splice(index, 1);
        }
    }

    totalPercentage(): number {
        return this.allocations.reduce((total, allocation) => total + (Number(allocation.percentage) || 0), 0);
    }

    isValid(): boolean {
        return validMeterAllocations(this.allocations);
    }

    save(): void {
        if (this.isValid()) {
            this.configure(this.allocations);
        }
    }

    confirmUnsplit(): void {
        openConfirmationModalWithCallback(confirmed => {
            if (confirmed) {
                this.configure([]);
            }
        }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
            type: 'danger',
            title: 'Remove meter split',
            message: 'This removes all allocated child meters and their authorisations. The source meter and its data remain unchanged.',
            question: 'Are you sure you want to remove this meter split?',
            confirmText: 'Remove meter split',
            cancelText: 'Cancel'
        }, 'static');
    }

    trackByMeterId = (_index: number, allocation: MeterAllocation): string => allocation.meterId;

    private configure(allocations: MeterAllocation[]): void {
        this.sendCommand('com.flowmaps.api.organisation.ConfigureMeterSplit',
            configureMeterSplitCommand(this.data.entity, allocations));
    }
}

export function canConfigureMeterSplit(entity: Entity, isAuthorised: boolean): boolean {
    const meter = entity?.meter;
    const meters = entity?.connection?.meters || [];
    return isAuthorised
        && entity?.connection?.info?.connectionType === ConnectionType.Electricity
        && meter?.info?.type === MeterType.INTERMEDIATE
        && meter.info?.virtual !== true
        && !isPercentageAllocated(meter)
        && !meters.some(child => !isPercentageAllocated(child)
            && (child.parentId === meter.meterId || meterDisplayParentId(child) === meter.meterId));
}

export function validMeterAllocations(allocations: MeterAllocation[]): boolean {
    const total = allocations.reduce((sum, allocation) => sum + (Number(allocation.percentage) || 0), 0);
    return allocations.length >= 2 && allocations.length <= MAX_ALLOCATIONS
        && allocations.every(allocation => validPercentage(allocation.percentage))
        && Math.round(total * 10) === 1000;
}

export function configureMeterSplitCommand(entity: Entity,
                                           allocations: MeterAllocation[]): ConfigureMeterSplit {
    return {
        organisationId: entity.organisation.organisationId,
        locationId: entity.location.locationId,
        connectionId: entity.connection.connectionId,
        meterId: entity.meter.meterId,
        allocations: cloneDeep(allocations)
    };
}

function allocatedChildren(source: Meter, meters: Meter[]): Meter[] {
    return (meters || []).filter(meter => meter.parentId === source.meterId && isPercentageAllocated(meter));
}

function newAllocation(percentage: number | undefined): MeterAllocation {
    return {meterId: uuid(), percentage, details: defaultDetails()};
}

function defaultDetails(): MeterDetails {
    return {type: MeterType.INTERMEDIATE, label: '', meterNumbers: [], tags: []};
}

function validPercentage(value: number): boolean {
    const percentage = Number(value);
    return Number.isFinite(percentage) && percentage > 0 && percentage <= 100
        && Math.abs(percentage * 10 - Math.round(percentage * 10)) < 1e-9;
}

export interface MeterSplitComponentData {
    entity: Entity;
}
